export const formatInr = (value, compact = false) => {
  if (!Number.isFinite(value)) return '—';
  if (compact) {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(value);
  }
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(value);
};

export const formatCrores = (value) => {
  if (!Number.isFinite(value)) return '—';
  if (value >= 100000) return `₹${(value / 100000).toFixed(2)}L Cr`;
  if (value >= 1000) return `₹${(value / 1000).toFixed(1)}K Cr`;
  return `₹${value.toFixed(0)} Cr`;
};

export const formatVolume = (value) =>
  new Intl.NumberFormat('en-IN', { notation: 'compact', maximumFractionDigits: 1 }).format(value || 0);

export const getPerformance = (stock, period) => {
  if (period === '1W') return stock.change1W;
  if (period === '1M') return stock.change1M;
  return stock.change;
};

export const changeColor = (change, muted = false) => {
  if (Math.abs(change) < 0.18) return muted ? '#596579' : '#475569';
  const intensity = Math.min(Math.abs(change) / 3, 1);
  if (change > 0) {
    return intensity > 0.72 ? '#0f7a5a' : intensity > 0.35 ? '#178f69' : '#277d67';
  }
  return intensity > 0.72 ? '#b63d52' : intensity > 0.35 ? '#9a4556' : '#76505a';
};

export const getMarketStats = (stocks, period = '1D') => {
  const changes = stocks.map((stock) => getPerformance(stock, period));
  const advances = changes.filter((change) => change > 0).length;
  const declines = changes.filter((change) => change < 0).length;
  const average = changes.reduce((sum, change) => sum + change, 0) / Math.max(changes.length, 1);
  const sectorScores = Object.entries(
    stocks.reduce((acc, stock) => {
      const score = acc[stock.sector] || { total: 0, count: 0 };
      score.total += getPerformance(stock, period);
      score.count += 1;
      acc[stock.sector] = score;
      return acc;
    }, {}),
  ).map(([name, score]) => ({ name, change: score.total / score.count }));

  sectorScores.sort((a, b) => b.change - a.change);
  return {
    advances,
    declines,
    unchanged: stocks.length - advances - declines,
    breadth: advances / Math.max(advances + declines, 1),
    average,
    sectors: sectorScores,
  };
};

export const deterministicSeries = (stock, points = 48) => {
  let seed = stock.symbol.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
  const random = () => {
    seed = (seed * 9301 + 49297) % 233280;
    return seed / 233280;
  };
  const end = stock.price;
  const start = end / (1 + stock.change1M / 100);
  return Array.from({ length: points }, (_, index) => {
    const progress = index / (points - 1);
    const trend = start + (end - start) * progress;
    const wave = Math.sin(index / 3.1) * end * 0.009;
    const noise = (random() - 0.5) * end * 0.012;
    return Number((trend + wave + noise).toFixed(2));
  });
};

export const safeLocalStorage = {
  read(key, fallback) {
    try {
      const value = localStorage.getItem(key);
      return value ? JSON.parse(value) : fallback;
    } catch {
      return fallback;
    }
  },
  write(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // The product remains usable when storage is unavailable.
    }
  },
  readMigrated(key, legacyKeys, fallback) {
    try {
      const currentValue = localStorage.getItem(key);
      if (currentValue !== null) return JSON.parse(currentValue);

      for (const legacyKey of legacyKeys) {
        const legacyValue = localStorage.getItem(legacyKey);
        if (legacyValue === null) continue;
        const parsedValue = JSON.parse(legacyValue);
        localStorage.setItem(key, JSON.stringify(parsedValue));
        return parsedValue;
      }

      return fallback;
    } catch {
      return fallback;
    }
  },
};
